import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatDateTime } from "../../lib/format";
import { XCircle, Users, UserCheck, History } from "lucide-react";

const CardHead = ({ icon: Icon, color, title }) => (
  <div className="flex items-center gap-2 mb-2"><Icon className={`h-4 w-4 ${color}`} /><h3 className="font-semibold text-slate-800 text-sm">{title}</h3></div>
);

const PartnerSelect = ({ testid, value, onChange, partners, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="bg-white sm:max-w-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
  </Select>
);

export const RejectedPartnersCard = ({ rejected }) => !rejected?.length ? null : (
  <Card className="p-4 border-[#942F30]/30 bg-[#942F30]/5 mb-6" data-testid="rejected-partners-card">
    <CardHead icon={XCircle} color="text-[#942F30]" title={`Rifiutato da ${rejected.length} partner`} />
    <div className="flex flex-wrap gap-2">
      {rejected.map((rd) => (
        <span key={rd.id} data-testid={`rejected-partner-${rd.id}`} className="inline-flex items-center gap-1.5 bg-white border border-[#942F30]/30 rounded-full px-3 py-1 text-xs text-slate-700">
          <span className="font-medium">{rd.name}</span>{rd.at && <span className="text-slate-400">· {formatDateTime(rd.at)}</span>}
        </span>
      ))}
    </div>
  </Card>
);

export const CandidatesCard = ({ candidates = [], busy, onAssign }) => (
  <Card className="p-5 border-primary/30 bg-primary/5 mb-6">
    <div className="mb-1"><CardHead icon={Users} color="text-primary" title={`Candidati (${candidates.length}) — Seleziona il Partner`} /></div>
    {candidates.length === 0 ? <p className="text-sm text-slate-500">Nessuna candidatura ricevuta finora.</p> : (
      <div className="space-y-2">
        {candidates.map((cd) => (
          <div key={cd.id} data-testid={`candidate-${cd.id}`} className="flex items-center justify-between bg-white rounded-md border border-slate-200 px-4 py-2.5">
            <div><p className="text-sm font-medium text-slate-800">{cd.name}</p><p className="text-xs text-slate-500">{cd.email}</p></div>
            <Button size="sm" data-testid={`assign-${cd.id}`} onClick={() => onAssign(cd.id)} disabled={busy}>Assegna</Button>
          </div>
        ))}
      </div>
    )}
  </Card>
);

export const DirectAssignCard = ({ partners, value, onChange, busy, onAssign }) => (
  <Card className="p-5 border-emerald-300 bg-emerald-50/60 mb-6" data-testid="direct-assign-card">
    <CardHead icon={UserCheck} color="text-emerald-700" title="Assegnazione Diretta" />
    <p className="text-xs text-slate-600 mb-3">Assegna direttamente la corsa a un partner, anche se era stata pubblicata con un'altra modalità (First Come First Served o Selezione Manuale). Il partner riceverà una notifica via email.</p>
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      <PartnerSelect testid="direct-assign-select" value={value} onChange={onChange} partners={partners} placeholder="Seleziona un partner" />
      <Button data-testid="direct-assign-button" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={!value || busy} onClick={() => onAssign(value, { direct: true, notify: true })}>
        <UserCheck className="h-4 w-4" /> Assegna Direttamente
      </Button>
    </div>
  </Card>
);

export const ReassignCard = ({ currentName, partners, value, onChange, note, setNote, busy, onReassign }) => (
  <Card className="p-5 border-amber-300 bg-amber-50/60 mb-6" data-testid="reassign-card">
    <CardHead icon={UserCheck} color="text-amber-700" title="Riassegna Partner" />
    <p className="text-xs text-slate-600 mb-3">Partner attuale: <b>{currentName || "—"}</b>. Puoi cambiare partner (es. se rinuncia). Il nuovo partner riceverà una notifica via email.</p>
    <Input data-testid="reassign-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Motivo della riassegnazione (facoltativo)" className="bg-white mb-2" />
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      <PartnerSelect testid="reassign-select" value={value} onChange={onChange} partners={partners} placeholder="Seleziona nuovo partner" />
      <Button data-testid="reassign-button" className="gap-2 bg-amber-600 hover:bg-amber-700" disabled={!value || busy} onClick={() => onReassign(value)}><UserCheck className="h-4 w-4" /> Riassegna</Button>
    </div>
  </Card>
);

export const ActivityLogCard = ({ activity }) => !activity?.length ? null : (
  <Card className="p-5 border-slate-200 mb-6" data-testid="activity-log">
    <div className="mb-1"><CardHead icon={History} color="text-primary" title="Storico Attività" /></div>
    <div className="space-y-3">
      {[...activity].reverse().map((a, i) => (
        <div key={`${a.at}-${i}`} data-testid="activity-entry" className="flex gap-3 text-sm">
          <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${a.type === "cancel" ? "bg-red-500" : "bg-amber-500"}`} />
          <div className="flex-1">
            <p className="text-slate-800 font-medium">{a.type === "cancel" ? "Annullamento" : "Riassegnazione"}{a.detail ? <span className="font-normal text-slate-500"> — {a.detail}</span> : null}</p>
            {a.note && <p className="text-slate-600 mt-0.5">Motivo: {a.note}</p>}
            <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(a.at)}{a.by ? ` · ${a.by}` : ""}</p>
          </div>
        </div>
      ))}
    </div>
  </Card>
);
